/**
 * Keeps Step 2's apartment list in sync with Step 1's apartment count
 * (COL-379). Must be called from a component that stays mounted for the
 * whole wizard — `Step2Apartments` itself is only rendered while
 * `current === 2` and remounts on every visit, so a ref kept there can't
 * tell "the count really changed since we last synced" apart from "we just
 * remounted with a stale `apartments.length` because the admin manually
 * added/removed an apartment." Only a real change to `aptCount` may
 * grow/shrink the list; manual add/remove must survive step navigation.
 */
import { useEffect, useRef } from "react";
import { MAX_ROOM_FILL } from "@/components/admin/v2/ApartmentEditSheet";
import {
  newApartment,
  newRoom,
  type WizardApartment,
  type WizardRoom,
  type WizardState,
} from "./wizardContext";

/** A room with nothing but its (auto-generated) title — no data the admin typed.
 *  An empty list is pristine too. Only pristine rooms may be replaced when the
 *  estimated count changes; the first real value entered freezes them. The title
 *  counts as auto when blank or still in the generated "… — Room N" shape (the
 *  same trailing marker Step 2 uses to tell a following title from a renamed one). */
function roomIsPristine(r: WizardRoom): boolean {
  const autoTitle = r.title.trim() === "" || / — Room \d+$/.test(r.title);
  return (
    autoTitle &&
    !r.price.trim() &&
    !r.size_sqm.trim() &&
    !r.floor.trim() &&
    !r.keynest_pickup_code.trim() &&
    !r.descriptionEn.trim() &&
    !r.descriptionDe.trim() &&
    r.images.length === 0
  );
}

export function useApartmentCountSync(
  state: WizardState,
  setApartments: (apartments: WizardApartment[]) => void
) {
  const lastSyncedAptCount = useRef<string | null>(null);

  useEffect(() => {
    const raw = state.property.aptCount;
    if (lastSyncedAptCount.current === raw) return;
    lastSyncedAptCount.current = raw;

    const count = Math.max(1, Math.floor(Number(raw) || 1));
    const apartments = state.apartments;

    if (apartments.length === 0) {
      const seeded: WizardApartment[] = Array.from({ length: count }).map(
        (_, i) => {
          const label = `Apartment ${i + 1}`;
          const apt = newApartment(label);
          if (count === 1) {
            const roomCount = Math.max(
              0,
              Math.floor(Number(state.property.estimatedRoomCount) || 0)
            );
            if (roomCount > 0) apt.numRooms = String(roomCount);
            apt.rooms = Array.from({ length: roomCount }).map((_, r) => ({
              ...newRoom(),
              title: `${label} — Room ${r + 1}`,
            }));
          }
          return apt;
        }
      );
      setApartments(seeded);
      return;
    }

    if (count === apartments.length) return;
    if (count > apartments.length) {
      const additions = Array.from({ length: count - apartments.length }).map(
        (_, i) => newApartment(`Apartment ${apartments.length + i + 1}`)
      );
      setApartments([...apartments, ...additions]);
    } else {
      setApartments(apartments.slice(0, count));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.property.aptCount]);

  // Step 1's "Estimated room count" only pre-fills rooms for the single-apartment
  // case. The seed above runs once, on mount, while that count is still empty —
  // so typing it afterwards did nothing (the field was effectively dead). Keep
  // the single apartment's room list in sync with the count here, for as long as
  // its rooms are all still pristine; the first room the admin fills in freezes
  // them (Step 2's own "Number of rooms" field then owns the list).
  const lastSyncedRoomCount = useRef<string | null>(null);
  useEffect(() => {
    const raw = state.property.estimatedRoomCount;
    if (lastSyncedRoomCount.current === raw) return;

    const aptCount = Math.max(
      1,
      Math.floor(Number(state.property.aptCount) || 1)
    );
    const apts = state.apartments;
    if (aptCount !== 1 || apts.length !== 1) return;
    const apt = apts[0];
    if (!apt.rooms.every(roomIsPristine)) return;

    const roomCount = Math.min(
      MAX_ROOM_FILL,
      Math.max(0, Math.floor(Number(raw) || 0))
    );
    lastSyncedRoomCount.current = raw;
    // Never clobber on a cleared/zero count — only (re)generate a real one.
    if (roomCount === 0) return;
    setApartments([
      {
        ...apt,
        numRooms: String(roomCount),
        rooms: Array.from({ length: roomCount }).map((_, r) => ({
          ...newRoom(),
          title: `${apt.label} — Room ${r + 1}`,
        })),
      },
    ]);
  }, [
    state.property.estimatedRoomCount,
    state.property.aptCount,
    state.apartments,
    setApartments,
  ]);
}
